//Dark black formatting
import React, { useRef, useEffect } from 'react';
import * as Slider from '@radix-ui/react-slider';

/** A single thumb + optional bubble above it */
function ThumbWithBubble({ label, hideBubble }) {
  return (
    <div className="relative flex items-center justify-center">
      {/* The circular thumb */}
      <div className="w-4 h-4 bg-white border border-zinc-600 rounded-full mt-4" />

      {/* The bubble, positioned absolutely above the circle */}
      {!hideBubble && (
        <div
          className="
            absolute
            bottom-full
            -mb-2
            left-1/2
            -translate-x-1/2
            bg-zinc-800
            text-white
            text-xs
            px-2
            py-1
            rounded
            shadow
            whitespace-nowrap
            text-center

            // Arrow pointing downward:
            after:content-['']
            after:absolute
            after:bottom-[-4px]
            after:left-1/2
            after:-translate-x-1/2
            after:border-l-[4px]
            after:border-r-[4px]
            after:border-t-[4px]
            after:border-l-transparent
            after:border-r-transparent
            after:border-t-zinc-800
          "
        >
          {label}
        </div>
      )}
    </div>
  );
}

export default function DiscreteRangeSlider({
  label,
  ratingValues,
  value,
  onValueChange,
  customLabels,
}) {
  /**
   * 1) Define hooks first, unconditionally.
   */
  const bubbleRef = useRef(null);

  /**
   * 2) Derive fallback/safe values if props are invalid
   */
  let isValid = true;
  let steps = 0;
  let minVal = 1;
  let maxVal = 1;

  // Check ratingValues
  if (Array.isArray(ratingValues) && ratingValues.length > 0) {
    steps = ratingValues.length;
  } else {
    isValid = false;
  }

  // Check value
  if (Array.isArray(value) && value.length === 2) {
    minVal = value[0];
    maxVal = value[1];
  } else {
    isValid = false;
  }

  // Check minVal/maxVal are within range
  if (isValid) {
    if (minVal < 1 || maxVal > steps || minVal > maxVal) {
      isValid = false;
    }
  }

  /**
   * 3) useEffect (unconditional). If data is invalid, we skip the logic inside.
   */
  useEffect(() => {
    if (!isValid) return;

    // If you needed bubble measurements:
    if (bubbleRef.current) {
      // const bubbleWidth = bubbleRef.current.offsetWidth;
      // ...
    }
  }, [isValid, minVal, maxVal]);

  /**
   * 4) If invalid, log + return null (after hooks).
   */
  if (!isValid) {
    console.error(
      'DiscreteRangeSlider: invalid props. ' +
        'Ensure ratingValues is a non-empty array and value is [minVal, maxVal] in [1..length].'
    );
    return null;
  }

  /**
   * 5) Now we can safely do normal logic
   */
  const leftLabel =
    customLabels?.[minVal - 1] ?? ratingValues[minVal - 1]?.range[0] ?? '';
  const rightLabel =
    customLabels?.[maxVal - 1] ?? ratingValues[maxVal - 1]?.range[1] ?? '';
  const shouldMergeLabels = (maxVal - minVal) <= 0;

  // Slider positions as % of track
  const leftPercent = ((minVal - 1) / steps) * 100;
  const rightPercent = (maxVal / steps) * 100;
  const centerPercent = (leftPercent + rightPercent) / 2;

  // Nudging the merged bubble if desired
  const biasLookup = { 1: 4, 2: 2, 3: -1, 4: -3, 5: -5 };
  const bias = biasLookup[minVal] ?? 0;
  const adjustedCenterPercent = centerPercent + bias;

  // Convert slider's [0..steps] scale -> [1..steps]
  const handleSliderChange = ([sliderMin, sliderMax]) => {
    if (typeof onValueChange === 'function') {
      onValueChange([sliderMin + 1, sliderMax]);
    }
  };

  /**
   * 6) Render
   */
  return (
      
    <div className="px-4 relative">
      <label className="block font-semibold -ml-11 text-zinc-700 text-lg mb-0">{label}</label>

      <div className="flex flex-col">
        <Slider.Root
          minStepsBetweenThumbs={1}
          value={[minVal - 1, maxVal]}
          onValueChange={handleSliderChange}
          min={0}
          max={steps}
          step={1}
          className="relative flex items-center select-none w-full h-20 "
        >
          {/* Track */}
          <Slider.Track className="relative grow h-4 rounded-full  -mb-4">
            <div className="absolute inset-0 flex gap-[1px]">
              {ratingValues.map((_, i) => {
                const discreteIndex = i + 1;
                const isActive =
                  discreteIndex >= minVal && discreteIndex <= maxVal;

                // diagonal for non-active segments
                const diagonalBg = {
                  backgroundImage:
                    'repeating-linear-gradient(45deg, #e4e4e7, #e4e4e7 1px, transparent 1px, transparent 4px)',
                };
                const segmentStyle = isActive ? {} : diagonalBg;
                const borderClass = isActive
                  ? 'border-transparent bg-sky-300'
                  : 'border-zinc-400';

                const roundLeft = i === 0 ? 'rounded-l' : '';
                const roundRight = i === steps - 1 ? 'rounded-r' : '';

                return (
                  <div key={discreteIndex} className="flex-1 h-full p-[1px]">
                    <div
                      style={segmentStyle}
                      className={`w-full h-full border-[1px] border-dashed ${borderClass} ${roundLeft} ${roundRight}`}
                    />
                  </div>
                );
              })}
            </div>
          </Slider.Track>

          {/* Left Thumb */}
          <Slider.Thumb className="relative z-10">
            <ThumbWithBubble label={leftLabel} hideBubble={shouldMergeLabels} />
          </Slider.Thumb>

          {/* Right Thumb */}
          <Slider.Thumb className="relative z-10">
            <ThumbWithBubble
              label={rightLabel}
              hideBubble={shouldMergeLabels}
            />
          </Slider.Thumb>
        </Slider.Root>

        {/* If thumbs overlap, render merged bubble */}
        {shouldMergeLabels && (
          <div
            ref={bubbleRef}
            className="
              pointer-events-none 
              absolute
              top-9
              bg-zinc-800
              text-white
              text-xs
              px-2
              py-1
              rounded
              shadow
              whitespace-nowrap
              text-center

              // Arrow pointing downward:
              after:content-['']
              after:absolute
              after:bottom-[-4px]
              after:left-1/2
              after:-translate-x-1/2
              after:border-l-[4px]
              after:border-r-[4px]
              after:border-t-[4px]
              after:border-l-transparent
              after:border-r-transparent
              after:border-t-zinc-800
            "
            style={{
              left: `${adjustedCenterPercent}%`,
              transform: 'translateX(-50%)',
            }}
          >
            {leftLabel}
          </div>
        )}
      </div>
    </div>
  );
}